// @djsem/shared — Observability, Replay, and Validation Evidence (Prompt 24).
//
// Explains WHY output happened, WHY something was blocked, and how to REPLAY a session
// deterministically. Reuses the older `DJSEMTraceEnvelope` shape in worldPacks.ts (which
// has different fields — traceId/timeIso/module/event/severity(4)) as a NEIGHBOR, not a
// replacement. This module defines the richer P24 envelope alongside, named to avoid the
// clash (rule 2: no duplicate incompatible schemas). Redaction is default-safe: no secrets,
// tokens, auth headers, or full lyrics in the log.

import { vArray, vObject, vString, type Validator, type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult } from "./validation";
import { SeqCounter } from "./runtime";
import { stableStringify } from "./prep";

// ---- contracts --------------------------------------------------------------

/** The kind of event a trace records — from acquisition through output and operator control. */
export type DJSEMTraceEventType =
  | "session.started" | "session.ended"
  | "acquisition.received" | "event.normalized" | "schema.validated" | "runtime_context.updated"
  | "track_match.attempted" | "track.matched" | "track_match.failed"
  | "semantic_event.generated" | "semantic_event.suppressed"
  | "themed_event.generated" | "themed_event.blocked"
  | "output_frame.produced"
  | "lighting.scene.resolved" | "adapter.dry_run.sent" | "renderer_status.updated"
  | "operator_command.received" | "operator_command.applied"
  | "safe_mode.enabled" | "blackout.enabled"
  | "warning" | "error" | "heartbeat";

/** Severity level of an observability trace. */
export type DJSEMObservabilitySeverity = "debug" | "info" | "warning" | "error" | "critical";

/**
 * A single redacted, replayable trace record explaining why something happened (or was
 * blocked/suppressed). Named `DJSEMObservabilityTrace*` to avoid clashing with the older
 * `DJSEMTraceEnvelope` in worldPacks.ts, which this module deliberately keeps as a neighbor
 * rather than replacing.
 */
export interface DJSEMObservabilityTrace {
  traceId: string;
  correlationId?: string;
  sessionId: string;
  eventId: string;
  eventType: DJSEMTraceEventType;
  createdAt: string;
  source: string;
  severity: DJSEMObservabilitySeverity;
  payload: unknown;
  redactions: string[];
  warnings: string[];
}

/** An ordered, replayable log of traces for a session, used to deterministically re-derive state. */
export interface DJSEMObservabilityReplayLog {
  replayId: string;
  sessionId: string;
  createdAt: string;
  source: "rehearsal" | "live" | "simulator" | "test_fixture";
  events: DJSEMObservabilityTrace[];
  featureAvailability: Record<string, unknown>;
  warnings: string[];
}

/** Manifest describing the files that make up a QA/support evidence bundle for a session. */
export interface DJSEMEvidenceBundleManifest {
  evidenceId: string;
  sessionId: string;
  createdAt: string;
  files: Array<{ path: string; type: "jsonl" | "json" | "md" | "png" | "mp4" | "txt"; required: boolean; hash?: string }>;
  validation: DJSEMValidationResult;
}

// ---- inputs -----------------------------------------------------------------

/** Input to {@link DJSEMObservabilityService.createTrace}. */
export interface CreateTraceInput {
  sessionId: string; eventType: DJSEMTraceEventType; source: string;
  severity?: DJSEMObservabilitySeverity; payload?: unknown;
  correlationId?: string; traceId?: string; eventId?: string; createdAt?: string;
  reason?: string; // used when the event is a blocked/suppressed one; folded into payload.reason
}
/** Input to {@link DJSEMObservabilityService.writeTrace}. */
export interface WriteTraceInput { trace: DJSEMObservabilityTrace; sink?: (line: string) => void; }
/** Input to {@link DJSEMObservabilityService.redactTrace}. */
export interface RedactTraceInput { trace: DJSEMObservabilityTrace; extraSecretKeys?: string[]; }
/** Input to {@link DJSEMObservabilityService.buildReplayLog}. */
export interface BuildReplayLogInput { sessionId: string; source: DJSEMObservabilityReplayLog["source"]; events: DJSEMObservabilityTrace[]; featureAvailability?: Record<string, unknown>; createdAt?: string; }
/** Input to {@link DJSEMObservabilityService.replay}. */
export interface ReplayInput { log: DJSEMObservabilityReplayLog; apply: (trace: DJSEMObservabilityTrace) => void; }
/** Result of {@link DJSEMObservabilityService.replay}. */
export interface ReplayResult { ok: boolean; applied: number; warnings: string[] }
/** Input to {@link DJSEMObservabilityService.createEvidenceBundle}. */
export interface CreateEvidenceBundleInput {
  sessionId: string;
  replayLog: DJSEMObservabilityReplayLog;
  extraFiles?: Array<{ path: string; type: DJSEMEvidenceBundleManifest["files"][number]["type"]; required?: boolean; hash?: string }>;
  createdAt?: string;
}

// ---- redaction --------------------------------------------------------------

// Keys whose values are always removed from a trace payload before it's written.
const SECRET_KEYS = new Set([
  "password", "token", "auth", "authorization", "authHeader",
  "apiKey", "api_key", "accessKey", "secret", "sessionToken", "cookie",
  "privateKey", "clientSecret",
  "lyrics", "fullLyrics", "transcriptText", "fullText", // lyrics/transcript body: refs only
  "filesystemPath", "absolutePath",
]);

function redactValue(value: unknown, extra: Set<string>, path: string, removed: string[]): unknown {
  if (Array.isArray(value)) return value.map((v, i) => redactValue(v, extra, `${path}[${i}]`, removed));
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const p = `${path}.${k}`;
      if (SECRET_KEYS.has(k) || extra.has(k)) { removed.push(p); continue; }
      out[k] = redactValue(v, extra, p, removed);
    }
    return out;
  }
  return value;
}

// Local FNV-1a — keeps @djsem/shared self-contained (no cyclic dep on analysis-contract).
function fnvHash(value: unknown): string {
  const s = stableStringify(value);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(16).padStart(8, "0");
}

// ---- validators -------------------------------------------------------------

const EVENT_TYPES: DJSEMTraceEventType[] = [
  "session.started", "session.ended",
  "acquisition.received", "event.normalized", "schema.validated", "runtime_context.updated",
  "track_match.attempted", "track.matched", "track_match.failed",
  "semantic_event.generated", "semantic_event.suppressed",
  "themed_event.generated", "themed_event.blocked",
  "output_frame.produced",
  "lighting.scene.resolved", "adapter.dry_run.sent", "renderer_status.updated",
  "operator_command.received", "operator_command.applied",
  "safe_mode.enabled", "blackout.enabled",
  "warning", "error", "heartbeat",
];

/** Validator for the {@link DJSEMObservabilityTrace} shape. */
export const vObservabilityTrace: Validator<DJSEMObservabilityTrace> = vObject(
  {
    traceId: vString({ min: 1 }), sessionId: vString({ min: 1 }), eventId: vString({ min: 1 }),
    eventType: vString({ enum: EVENT_TYPES as unknown as string[] }),
    createdAt: vString({ min: 1 }), source: vString({ min: 1 }),
    severity: vString({ enum: ["debug", "info", "warning", "error", "critical"] }),
    redactions: vArray(vString({})), warnings: vArray(vString({})),
  },
  {
    correlationId: vString({}),
    // payload can be anything; treat as opaque here.
    payload: (v) => ({ valid: true, issues: [] }),
  },
) as Validator<DJSEMObservabilityTrace>;

// ---- service (class-based; hard rule 12) ------------------------------------

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

/**
 * Creates, redacts, writes, and replays observability traces — explaining WHY output
 * happened or was blocked, and enabling deterministic session REPLAY. Redaction is
 * default-safe: secrets, tokens, auth headers, and full lyrics/transcripts never reach a trace.
 */
export class DJSEMObservabilityService {
  /**
   * Creates a new trace record, redacting any secret-looking payload keys by default.
   * @param input - The session/event/source/severity and optional payload/reason/ids.
   * @returns The new (redacted) trace.
   */
  createTrace(input: CreateTraceInput): DJSEMObservabilityTrace {
    const rawPayload = input.reason !== undefined
      ? { ...(input.payload && typeof input.payload === "object" ? input.payload as object : {}), reason: input.reason }
      : (input.payload ?? {});
    const removed: string[] = [];
    const payload = redactValue(rawPayload, new Set(), "$", removed);
    return {
      traceId: input.traceId ?? `tr_${input.sessionId}_${_seq.next()}`,
      correlationId: input.correlationId,
      sessionId: input.sessionId,
      eventId: input.eventId ?? `ev_${input.sessionId}_${_seq.peek()}`,
      eventType: input.eventType,
      createdAt: input.createdAt ?? AT,
      source: input.source,
      severity: input.severity ?? "info",
      payload,
      redactions: removed,
      warnings: [],
    };
  }

  /**
   * Serializes a trace to a single JSON line and passes it to a sink (or discards it if no
   * sink is given).
   * @param input - The trace to write and an optional sink function.
   */
  async writeTrace(input: WriteTraceInput): Promise<void> {
    const line = JSON.stringify(input.trace);
    (input.sink ?? ((s: string) => { void s; }))(line + "\n");
  }

  /**
   * Explicit re-redaction — lets the caller add extra secret keys to strip at write time,
   * beyond the module's default `SECRET_KEYS` set.
   * @param input - The trace to redact and any extra secret key names to strip.
   * @returns A new trace with the additional keys removed from its payload.
   */
  redactTrace(input: RedactTraceInput): DJSEMObservabilityTrace {
    const extra = new Set(input.extraSecretKeys ?? []);
    const removed: string[] = [];
    const payload = redactValue(input.trace.payload, extra, "$", removed);
    return { ...input.trace, payload, redactions: [...input.trace.redactions, ...removed] };
  }

  /**
   * Builds a deterministic replay log from a set of traces, sorted by (createdAt, eventId)
   * regardless of input order.
   * @param input - The session id, source, events, and optional feature-availability snapshot.
   * @returns The new replay log, with a content-derived `replayId`.
   */
  buildReplayLog(input: BuildReplayLogInput): DJSEMObservabilityReplayLog {
    // Deterministic: events sorted by (createdAt, eventId).
    const events = [...input.events].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.eventId.localeCompare(b.eventId));
    const bodyHash = fnvHash({ sessionId: input.sessionId, source: input.source, events });
    return {
      replayId: `rp_${input.sessionId}_${bodyHash}`,
      sessionId: input.sessionId, createdAt: input.createdAt ?? AT, source: input.source,
      events, featureAvailability: input.featureAvailability ?? {}, warnings: [],
    };
  }

  /**
   * Replays a validated log by invoking `apply` for each event in order. A single event's
   * `apply` throwing is recorded as a warning rather than aborting the whole replay.
   * @param input - The replay log and the `apply` callback to run per event.
   * @returns Whether the log was valid, how many events were applied, and any warnings.
   */
  async replay(input: ReplayInput): Promise<ReplayResult> {
    const warnings: string[] = [];
    const v = this.validateReplayLog(input.log);
    if (!v.ok) return { ok: false, applied: 0, warnings: v.issues.map((i) => i.message) };
    let applied = 0;
    for (const ev of input.log.events) {
      try { input.apply(ev); applied++; }
      catch (e) { warnings.push(`apply threw on ${ev.eventId}: ${e instanceof Error ? e.message : String(e)}`); }
    }
    return { ok: true, applied, warnings };
  }

  /**
   * Builds a manifest for a QA/support evidence bundle (topology, runtime version, feature
   * availability, event log, replay log, and any extra files).
   * @param input - The session id, replay log, and optional extra files to include.
   * @returns The bundle manifest, with a validation result flagging any required file missing a hash.
   */
  createEvidenceBundle(input: CreateEvidenceBundleInput): Promise<DJSEMEvidenceBundleManifest> {
    const files: DJSEMEvidenceBundleManifest["files"] = [
      { path: "topology.md", type: "md", required: true, hash: fnvHash("topology") },
      { path: "runtime-version.txt", type: "txt", required: true, hash: fnvHash("runtime") },
      { path: "feature-availability.json", type: "json", required: true, hash: fnvHash(input.replayLog.featureAvailability) },
      { path: "event-log.jsonl", type: "jsonl", required: true, hash: fnvHash(input.replayLog.events) },
      { path: "replay.json", type: "json", required: true, hash: fnvHash(input.replayLog) },
      { path: "summary.md", type: "md", required: false },
      ...(input.extraFiles ?? []).map((f) => ({ ...f, required: !!f.required })),
    ];
    const validation = toDJSEMValidationResult({
      valid: files.filter((f) => f.required).every((f) => !!f.hash),
      issues: files.filter((f) => f.required && !f.hash).map((f) => ({ path: `$.files.${f.path}`, message: "required file missing hash" })),
    }, "evidence_bundle");
    return Promise.resolve({
      evidenceId: `ev_${input.sessionId}_${fnvHash({ files, sessionId: input.sessionId })}`,
      sessionId: input.sessionId, createdAt: input.createdAt ?? AT, files, validation,
    });
  }

  /**
   * Validates a replay log's shape, including every contained trace against
   * {@link vObservabilityTrace}.
   * @param log - The replay log to validate.
   * @returns The validation result.
   */
  validateReplayLog(log: DJSEMObservabilityReplayLog): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!log?.sessionId) issues.push({ path: "$.sessionId", message: "required" });
    if (!Array.isArray(log?.events)) issues.push({ path: "$.events", message: "events must be an array" });
    for (let i = 0; i < (log?.events ?? []).length; i++) {
      const r = vObservabilityTrace(log.events[i]);
      if (!r.valid) for (const iss of r.issues) issues.push({ path: `$.events[${i}]${iss.path.replace("$", "")}`, message: iss.message });
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "replay_log");
  }
}
